import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { 
  MessageCircle, Send, ArrowLeft, RefreshCw, Flag, 
  MoreVertical, Check, CheckCheck, Loader2, User
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import UserAvatar from '@/components/UserAvatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import api from '@/lib/api';

export default function MessagesPage() {
  const [conversations, setConversations] = useState([]);
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [thread, setThread] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showMobileThread, setShowMobileThread] = useState(false);
  const [flagDialog, setFlagDialog] = useState({ open: false, messageId: null });
  const [flagReason, setFlagReason] = useState('');
  
  const messagesEndRef = useRef(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Check for direct message from match card
  const directUserId = searchParams.get('to');

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    // If directed to message a specific user
    if (directUserId && conversations.length >= 0) {
      openThread(directUserId);
      setShowMobileThread(true);
    }
  }, [directUserId, conversations]);

  useEffect(() => {
    // Scroll to bottom when thread changes
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [thread?.messages]);

  const fetchConversations = async () => {
    try {
      const response = await api.get('/messages/conversations');
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const openThread = async (userId) => {
    try {
      const response = await api.get(`/messages/thread/${userId}`);
      setThread(response.data);
      setSelectedConvo(userId);
      
      // Refresh conversations to update unread count
      fetchConversations();
    } catch (error) {
      toast.error('Failed to load conversation');
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchConversations();
    if (selectedConvo) {
      await openThread(selectedConvo);
    }
    setRefreshing(false);
    toast.success('Messages refreshed');
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConvo) return;
    
    setSendingMessage(true);
    try {
      const response = await api.post('/messages/send', {
        recipient_id: selectedConvo,
        body: newMessage.trim()
      });
      
      // Add message to thread
      setThread(prev => ({
        ...prev,
        messages: [...(prev?.messages || []), response.data],
        is_first_message: false
      }));
      
      setNewMessage('');
      
      // Refresh conversations to update last message
      fetchConversations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleFlagMessage = async () => {
    if (!flagDialog.messageId) return;
    
    try {
      await api.post(`/messages/${flagDialog.messageId}/flag`, {
        message_id: flagDialog.messageId,
        reason: flagReason
      });
      toast.success('Message flagged for review');
      setFlagDialog({ open: false, messageId: null });
      setFlagReason('');
    } catch (error) {
      toast.error('Failed to flag message');
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diff < 604800000) return date.toLocaleDateString([], { weekday: 'short' });
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const formatMessageTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-warm-white flex items-center justify-center">
        <Loader2 className="animate-spin text-teal" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warm-white font-body">
      {/* Header */}
      <header className="bg-white border-b border-border-subtle sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-navy hover:text-teal transition-colors"
              data-testid="messages-back-btn"
            >
              <ArrowLeft size={22} />
            </button>
            {showMobileThread && (
              <button 
                onClick={() => {
                  setShowMobileThread(false);
                  setSelectedConvo(null);
                  setThread(null);
                  navigate('/messages');
                }}
                className="md:hidden text-navy"
              >
                <ArrowLeft size={24} />
              </button>
            )}
            <h1 className="font-display text-2xl text-navy">Messages</h1>
          </div>
          <Button
            variant="ghost"
            onClick={handleRefresh}
            disabled={refreshing}
            className="text-teal"
            data-testid="refresh-messages-btn"
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
            <span className="ml-2 hidden sm:inline">Refresh</span>
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto">
        <div className="flex h-[calc(100vh-73px)]">
          {/* Conversations List - Hidden on mobile when thread is open */}
          <div className={`w-full md:w-1/3 border-r border-border-subtle bg-white overflow-y-auto ${showMobileThread ? 'hidden md:block' : ''}`}>
            {conversations.length === 0 ? (
              <div className="p-8 text-center">
                <MessageCircle className="mx-auto text-seafoam mb-4" size={48} />
                <h3 className="font-semibold text-navy mb-2">No messages yet</h3>
                <p className="text-text-mid text-sm">
                  Start a conversation from your matches on the dashboard!
                </p>
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="mt-4 bg-teal hover:bg-teal-deep text-white rounded-full"
                >
                  View Matches
                </Button>
              </div>
            ) : (
              conversations.map(conv => (
                <button
                  key={conv.user.id}
                  onClick={() => {
                    openThread(conv.user.id);
                    setShowMobileThread(true);
                  }}
                  data-testid={`conversation-${conv.user.id}`}
                  className={`w-full p-4 flex items-start gap-3 border-b border-border-subtle hover:bg-cream/50 transition-colors text-left ${
                    selectedConvo === conv.user.id ? 'bg-seafoam/20' : ''
                  }`}
                >
                  {/* Avatar */}
                  <div className="relative flex-shrink-0">
                    <UserAvatar
                      src={conv.user.profile_photo_url}
                      firstName={conv.user.first_name}
                      lastName={conv.user.last_name}
                      size="w-12 h-12"
                      textSize="text-base"
                    />
                    {conv.unread_count > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-teal rounded-full flex items-center justify-center text-white text-xs font-bold">
                        {conv.unread_count > 9 ? '9+' : conv.unread_count}
                      </span>
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-navy truncate">
                        {conv.user.first_name} {conv.user.last_name?.[0]}.
                      </span>
                      <span className="text-xs text-text-light flex-shrink-0">
                        {formatTime(conv.last_message.sent_at)}
                      </span>
                    </div>
                    <p className={`text-sm truncate ${conv.unread_count > 0 ? 'text-navy font-medium' : 'text-text-mid'}`}>
                      {conv.last_message.is_mine && <span className="text-text-light">You: </span>}
                      {conv.last_message.body}
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Thread Panel */}
          <div className={`flex-1 flex flex-col bg-cream/30 ${!showMobileThread && !selectedConvo ? 'hidden md:flex' : ''} ${showMobileThread || selectedConvo ? '' : 'hidden md:flex'}`}>
            {thread ? (
              <>
                {/* Thread Header */}
                <div className="bg-white border-b border-border-subtle p-4 flex items-center gap-3">
                  <UserAvatar
                    src={thread.other_user.profile_photo_url}
                    firstName={thread.other_user.first_name}
                    lastName={thread.other_user.last_name}
                    size="w-10 h-10"
                    textSize="text-sm"
                  />
                  <div>
                    <h3 className="font-semibold text-navy">
                      {thread.other_user.first_name} {thread.other_user.last_name?.[0]}.
                    </h3>
                    <p className="text-xs text-text-mid">{thread.other_user.city}</p>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {/* First message prompt */}
                  {thread.is_first_message && thread.is_matched && (
                    <div className="bg-white rounded-2xl p-6 text-center shadow-sm mb-6">
                      <div className="w-16 h-16 bg-seafoam/30 rounded-full flex items-center justify-center mx-auto mb-4">
                        <MessageCircle className="text-teal" size={28} />
                      </div>
                      <h3 className="font-display text-xl text-navy mb-2">
                        You and {thread.other_user.first_name} are a match!
                      </h3>
                      <p className="text-text-mid">
                        Say hello and start the conversation.
                      </p>
                    </div>
                  )}

                  {thread.messages.map((msg, idx) => {
                    const isMine = msg.sender_id === user?.id;
                    return (
                      <div 
                        key={msg.id || idx}
                        className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[75%] ${isMine ? 'order-1' : ''}`}>
                          <div 
                            className={`rounded-2xl px-4 py-3 ${
                              isMine 
                                ? 'bg-teal text-white rounded-br-md' 
                                : 'bg-white text-navy rounded-bl-md shadow-sm'
                            }`}
                          >
                            <p className="whitespace-pre-wrap break-words">{msg.body}</p>
                          </div>
                          <div className={`flex items-center gap-2 mt-1 ${isMine ? 'justify-end' : ''}`}>
                            <span className="text-xs text-text-light">
                              {formatMessageTime(msg.sent_at)}
                            </span>
                            {isMine && (
                              msg.read_at ? (
                                <CheckCheck size={14} className="text-teal" />
                              ) : (
                                <Check size={14} className="text-text-light" />
                              )
                            )}
                            {!isMine && !msg.flagged && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button className="text-text-light hover:text-navy">
                                    <MoreVertical size={14} />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent>
                                  <DropdownMenuItem 
                                    onClick={() => setFlagDialog({ open: true, messageId: msg.id })}
                                    className="text-red-600"
                                  >
                                    <Flag size={14} className="mr-2" />
                                    Report Message
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                            {msg.flagged && !isMine && (
                              <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">
                                Reported
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <form onSubmit={handleSendMessage} className="bg-white border-t border-border-subtle p-4">
                  <div className="flex items-center gap-3">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      className="flex-1 rounded-full border-seafoam focus:border-teal"
                      data-testid="message-input"
                      maxLength={2000}
                    />
                    <Button
                      type="submit"
                      disabled={!newMessage.trim() || sendingMessage}
                      className="bg-teal hover:bg-teal-deep text-white rounded-full px-6"
                      data-testid="send-message-btn"
                    >
                      {sendingMessage ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <Send size={18} />
                      )}
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center p-8">
                  <MessageCircle className="mx-auto text-seafoam mb-4" size={64} />
                  <h3 className="font-display text-xl text-navy mb-2">Select a conversation</h3>
                  <p className="text-text-mid">Choose a conversation from the list to start chatting</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Flag Message Dialog */}
      <Dialog open={flagDialog.open} onOpenChange={(open) => setFlagDialog({ open, messageId: flagDialog.messageId })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report Message</DialogTitle>
            <DialogDescription>
              This message will be reviewed by our team. Please provide a reason for your report.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={flagReason}
            onChange={(e) => setFlagReason(e.target.value)}
            placeholder="Why are you reporting this message? (optional)"
            className="min-h-[100px]"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setFlagDialog({ open: false, messageId: null })}>
              Cancel
            </Button>
            <Button onClick={handleFlagMessage} className="bg-red-600 hover:bg-red-700 text-white">
              Report Message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
